/** @format */

import { AbstractControl, ValidatorFn } from '@angular/forms';
import { leadingZeroDoubleDigits, singleDigits } from './special-char-validator.constants';

import { PersonalizedNumberRange } from '@pdp/data-access/enums';

export interface ValidationResponse {
	message: string;
	valid: boolean;
}

export interface ValidateSpecialCharValidation {
	validateSpecialChar: ValidationResponse;
}

export interface ValidateNumberValidation {
	validateNumber: ValidationResponse;
}

export const validateSpecialChar = (
	control: AbstractControl
): ValidateSpecialCharValidation | null => {
	if (control.value && !control.value.match(/^([\w\s_&.'?!$#@]*)$/g)) {
		return {
			validateSpecialChar: {
				valid: false,
				message: `Can only contain these special characters: & _ . ' ? ! $ # @ `
			}
		};
	}
	return null;
};

export const validateBannedWord =
	(list: string[]): ValidatorFn =>
	(control: AbstractControl): ValidateSpecialCharValidation | null => {
		// Ensure we have something to compare
		if (
			typeof control.value === 'string' &&
			control.value.trim() !== '' &&
			Array.isArray(list) &&
			list.length > 0
		) {
			// Phrase words
			const phraseWords = control.value.toLowerCase().trim().split(' ');

			// Iterate through the array of banned words as the outer loop so we check every possible banned word against supplied phrase
			for (
				let arrayOfBannedWordsIndex = 0, arrayOfBannedWordsLength = list.length;
				arrayOfBannedWordsIndex < arrayOfBannedWordsLength;
				arrayOfBannedWordsIndex += 1
			) {
				// Iterate through the phrase words
				for (
					let phraseWordsIndex = 0, phraseWordsLength = phraseWords.length;
					phraseWordsIndex < phraseWordsLength;
					phraseWordsIndex += 1
				) {
					// Compare the current banned word against the current phrase word
					if (
						typeof list[arrayOfBannedWordsIndex] === 'string' &&
						list[arrayOfBannedWordsIndex].toLowerCase().trim() ===
							phraseWords[phraseWordsIndex].trim()
					) {
						// There is a matching word, don't perform any further checks and return true
						return {
							validateSpecialChar: {
								valid: false,
								message: 'This word is not allowed.'
							}
						};
					}
				}
			}
		}
	};

export const validateRequired = (
	control: AbstractControl
): ValidateSpecialCharValidation | null => {
	if (!control.value || control.value.trim() === '') {
		return {
			validateSpecialChar: {
				valid: false,
				message: 'This field is required.'
			}
		};
	}
	return null;
};

export const validateNumberRequired =
	(requiredLength: number): ValidatorFn =>
	(control: AbstractControl): ValidateNumberValidation | null => {
		if (!control.value || control.value.length !== requiredLength) {
			return {
				validateNumber: {
					valid: false,
					message: 'This field is required.'
				}
			};
		}
	};

export const validateNumber =
	(requiredLength: number, numberRange: PersonalizedNumberRange): ValidatorFn =>
	(control: AbstractControl): ValidateNumberValidation | null => {
		if (!control.value) {
			return null;
		}

		let numberValidationResponse = null;
		const validationLogic = (
			validValues: string[],
			message: string,
			exclude = false
		): ValidateNumberValidation | null => {
			const isValueInvalid = exclude
				? validValues.indexOf(control.value) !== -1
				: validValues.indexOf(control.value) === -1;
			if (isValueInvalid || control.value.length !== requiredLength) {
				return {
					validateNumber: {
						valid: false,
						message
					}
				};
			}
			return null;
		};

		if (typeof numberRange === 'string') {
			switch (numberRange) {
				case PersonalizedNumberRange.SINGLE_LOW:
					numberValidationResponse = validationLogic(
						singleDigits.low,
						'Please enter 1-4 only.'
					);
					break;

				case PersonalizedNumberRange.SINGLE_ALL:
					numberValidationResponse = validationLogic(
						singleDigits.all,
						'Please enter 1-9 only.'
					);
					break;

				case PersonalizedNumberRange.DOUBLE:
					numberValidationResponse = validationLogic(
						leadingZeroDoubleDigits,
						'Please enter 00 or 10-99 only.',
						true
					);
					break;
			}
			// Legacy product setup
		} else if (requiredLength === 2) {
			numberValidationResponse = validationLogic(
				leadingZeroDoubleDigits,
				'Please enter 00 or 10-99 only.',
				true
			);
		} else if (requiredLength === 1) {
			numberValidationResponse = validationLogic(singleDigits.low, 'Please enter 1-4 only.');
		}

		return numberValidationResponse;
	};
